// src/intervalMath.ts
import { dateToLocalDate, toLocalDate } from "@rock/util";
function addInterval(date, interval) {
  const result = new Date(date);
  const { value, unit } = interval;
  switch (unit) {
    case "days":
      result.setDate(result.getDate() + value);
      break;
    case "weeks":
      result.setDate(result.getDate() + value * 7);
      break;
    case "months":
      result.setMonth(result.getMonth() + value);
      break;
    case "years":
      result.setFullYear(result.getFullYear() + value);
      break;
  }
  return result;
}
function lastPerformedEstimateToInterval(estimate) {
  switch (estimate) {
    case "last-few-weeks":
      return { value: 30, unit: "days" };
    case "last-few-months":
      return { value: 75, unit: "days" };
    case "within-last-year":
      return { value: 8, unit: "months" };
    case "more-than-a-year":
      return { value: 12, unit: "months" };
    case "never":
    case "not-sure":
    // `specific-date` carries its own `lastPerformedAt` (see resolveLastPerformedAt) —
    // there's no coarse interval to derive from the bucket alone.
    case "specific-date":
      return null;
  }
}
function estimatedLastPerformedAt(createdAt, estimate) {
  if (!estimate) return null;
  const interval = lastPerformedEstimateToInterval(estimate);
  if (!interval) return null;
  const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return addInterval(created, { value: -interval.value, unit: interval.unit });
}
function resolveLastPerformedAt(args) {
  if (args.lastPerformedEstimate === "specific-date") {
    return args.lastPerformedAt ? toLocalDate(args.lastPerformedAt) : null;
  }
  const estimated = estimatedLastPerformedAt(args.createdAt, args.lastPerformedEstimate);
  return estimated ? dateToLocalDate(estimated) : null;
}

// src/seasonalWindow.ts
import {
  LocalDateSchema
} from "@rock/types";
import { addDaysToLocalDate } from "@rock/util";
var SEASON_PART_MONTHS = {
  "early-spring": [3, 4],
  spring: [3, 4, 5],
  "late-spring": [4, 5],
  "early-summer": [6, 7],
  summer: [6, 7, 8],
  "late-summer": [7, 8],
  "early-fall": [9, 10],
  fall: [9, 10, 11],
  "late-fall": [10, 11],
  "early-winter": [12, 1],
  winter: [12, 1, 2],
  "late-winter": [1, 2]
};
function isInWindow(date, window) {
  return windowMonths(window).has(monthOf(date));
}
function snapToWindow(base, window, floor) {
  const date = base > floor ? base : floor;
  const months = windowMonths(window);
  if (months.has(monthOf(date))) return date;
  const instances = runInstancesAround(date, months);
  const missed = instances.filter((run) => run.end < date).at(-1);
  if (missed && missed.end >= floor) return missed.start > floor ? missed.start : floor;
  const next = instances.find((run) => run.start > date);
  return next?.start ?? date;
}
function windowRunEnd(date, window) {
  const months = windowMonths(window);
  if (!months.has(monthOf(date))) return void 0;
  return runInstancesAround(date, months).find((run) => run.start <= date && date <= run.end)?.end;
}
function windowFitsCadence(cadence) {
  if (!cadence.window?.length || cadence.type === "one-time") return true;
  const { value, unit } = cadence.interval;
  if (unit === "years" || unit === "months" && value % 12 === 0) return true;
  if (unit !== "months" || 12 % value !== 0) return false;
  return runsOf(windowMonths(cadence.window)).length === 12 / value;
}
function withFittingWindow(cadence) {
  return windowFitsCadence(cadence) ? cadence : { ...cadence, window: void 0 };
}
function windowsEqual(a, b) {
  const left = [...windowMonths(a ?? [])].sort((x, y) => x - y);
  const right = [...windowMonths(b ?? [])].sort((x, y) => x - y);
  return left.length === right.length && left.every((month, i) => month === right[i]);
}
function seasonLabel(date, window) {
  const month = monthOf(date);
  const part = window.filter((p) => SEASON_PART_MONTHS[p].includes(month)).sort((a, b) => SEASON_PART_MONTHS[a].length - SEASON_PART_MONTHS[b].length)[0];
  return part && titleCase(part);
}
function formatCadenceWindow(cadence) {
  if (!cadence.window?.length) return void 0;
  const each = cadence.type === "recurring" && cadence.interval.unit !== "years";
  return joinWords(
    cadence.window.map((part) => part.replace("-", " ")),
    each ? "and" : "or"
  );
}
function windowMonths(window) {
  return new Set(window.flatMap((part) => SEASON_PART_MONTHS[part] ?? []));
}
function runsOf(months) {
  if (months.size === 12) return [{ startMonth: 1, length: 12 }];
  const runs = [];
  for (let month = 1; month <= 12; month++) {
    if (!months.has(month) || months.has(previousMonth(month))) continue;
    let length = 1;
    while (months.has((month + length - 1) % 12 + 1)) length++;
    runs.push({ startMonth: month, length });
  }
  return runs;
}
function runInstancesAround(date, months) {
  const year = Number(date.slice(0, 4));
  const instances = [];
  for (let y = year - 2; y <= year + 1; y++) {
    for (const { startMonth, length } of runsOf(months)) {
      const start = firstOfMonth(y, startMonth);
      const end = addDaysToLocalDate(firstOfMonth(y, startMonth + length), -1);
      instances.push({ start, end });
    }
  }
  return instances.sort((a, b) => a.start < b.start ? -1 : a.start > b.start ? 1 : 0);
}
function firstOfMonth(year, month) {
  const y = year + Math.floor((month - 1) / 12);
  const m = (month - 1) % 12 + 1;
  return LocalDateSchema.parse(`${y}-${`${m}`.padStart(2, "0")}-01`);
}
function monthOf(date) {
  return Number(date.slice(5, 7));
}
function previousMonth(month) {
  return month === 1 ? 12 : month - 1;
}
function titleCase(part) {
  return part.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
function joinWords(words, conjunction) {
  if (words.length <= 2) return words.join(` ${conjunction} `);
  return `${words.slice(0, -1).join(", ")} ${conjunction} ${words.at(-1)}`;
}

// src/calculateNextTaskDate.ts
import { addDaysToLocalDate as addDaysToLocalDate2, addIntervalToLocalDate, daysBetweenLocalDates, toLocalDate as toLocalDate2 } from "@rock/util";
function calculateNextTaskDate(subscription, createdAt, lastGeneratedAt, lastPerformedAt) {
  const createdDay = toLocalDate2(createdAt);
  if (subscription.type === "one-time") {
    if (lastGeneratedAt) {
      return null;
    }
    return firstOccurrenceFrom(subscription, createdDay);
  }
  if (subscription.type === "recurring") {
    if (lastGeneratedAt) {
      return nextOccurrenceAfter(subscription, toLocalDate2(lastGeneratedAt), createdDay);
    }
    if (lastPerformedAt) {
      return nextOccurrenceAfter(subscription, toLocalDate2(lastPerformedAt), createdDay);
    }
    return firstOccurrenceFrom(subscription, createdDay);
  }
  return null;
}
function nextOccurrenceAfter(cadence, anchor, notBefore) {
  const base = addIntervalToLocalDate(anchor, cadence.interval);
  if (!cadence.window?.length) return base;
  const gap = halfIntervalAfter(anchor, cadence.interval);
  return snapToWindow(
    withinTargetMonth(base, anchor, cadence.interval),
    cadence.window,
    notBefore && notBefore > gap ? notBefore : gap
  );
}
function firstOccurrenceDate(cadence, createdAt, lastPerformedEstimate) {
  const lastPerformedAt = cadence.type === "recurring" ? resolveLastPerformedAt({ createdAt, lastPerformedEstimate }) : null;
  return calculateNextTaskDate(cadence, createdAt, void 0, lastPerformedAt);
}
function firstOccurrenceRange(cadence, createdAt, lastPerformedEstimate) {
  if (!cadence.window?.length) return {};
  const first = firstOccurrenceDate(cadence, createdAt, lastPerformedEstimate);
  if (!first) return {};
  return { notBefore: first, notAfter: windowRunEnd(first, cadence.window) };
}
function resolveNextTaskDate(subscription) {
  return subscription.nextTaskDate ?? calculateNextTaskDate(
    subscription.subscription,
    subscription.createdAt,
    subscription.lastGeneratedAt,
    resolveLastPerformedAt(subscription)
  );
}
function upcomingTaskDate(subscription, today) {
  const nextTaskDate = subscription?.nextTaskDate;
  return nextTaskDate && nextTaskDate > today ? nextTaskDate : void 0;
}
function firstOccurrenceFrom(cadence, createdDay) {
  const start = cadence.offset ? addIntervalToLocalDate(createdDay, cadence.offset) : createdDay;
  return cadence.window?.length ? snapToWindow(start, cadence.window, start) : start;
}
function withinTargetMonth(base, anchor, interval) {
  if (interval.unit !== "months" && interval.unit !== "years") return base;
  const day = Number(base.slice(8, 10));
  return day < Number(anchor.slice(8, 10)) ? addDaysToLocalDate2(base, -day) : base;
}
function halfIntervalAfter(anchor, interval) {
  const days = daysBetweenLocalDates(addIntervalToLocalDate(anchor, interval), anchor);
  return addDaysToLocalDate2(anchor, Math.floor(days / 2));
}

// src/clampTaskDateForward.ts
function clampTaskDateForward(date, today) {
  return date < today ? today : date;
}

// src/comingUp.ts
import { addDaysToLocalDate as addDaysToLocalDate3 } from "@rock/util";
var COMING_UP_WINDOW_DAYS = 30;
function isComingUpChore(task, today, windowDays = COMING_UP_WINDOW_DAYS) {
  return task.status === "pending" && task.taskDate > today && task.taskDate <= addDaysToLocalDate3(today, windowDays);
}

// src/cascadeReschedule.ts
import { addDaysToLocalDate as addDaysToLocalDate4, daysBetweenLocalDates as daysBetweenLocalDates2 } from "@rock/util";
function cascadeReschedule(input) {
  const { task, newTaskDate, subscription, futurePendingTasks, nowIso } = input;
  if (!task.subscriptionId || subscription.subscription.type !== "recurring") {
    return {
      updatedTask: { ...task, taskDate: newTaskDate, updatedAt: nowIso },
      updatedFutureTasks: [],
      subscriptionNextTaskDate: null
    };
  }
  const deltaDays = daysBetweenLocalDates2(newTaskDate, task.taskDate);
  const updatedTask = {
    ...task,
    taskDate: newTaskDate,
    updatedAt: nowIso
  };
  const updatedFutureTasks = futurePendingTasks.map((futureTask) => ({
    ...futureTask,
    taskDate: addDaysToLocalDate4(futureTask.taskDate, deltaDays),
    updatedAt: nowIso
  }));
  const allShiftedDates = [newTaskDate, ...updatedFutureTasks.map((t) => t.taskDate)];
  const latestTaskDate = allShiftedDates.reduce((a, b) => a > b ? a : b);
  const subscriptionNextTaskDate = nextOccurrenceAfter(subscription.subscription, latestTaskDate);
  return {
    updatedTask,
    updatedFutureTasks,
    subscriptionNextTaskDate
  };
}

// src/pickWeekendRows.ts
import { isActNowCriticality as isActNowCriticality2 } from "@rock/util";

// src/planReviewSelection.ts
import { ConsiderationCriticalityOptions } from "@rock/types";
function planReviewSelection(items, options) {
  const actNow = items.filter((item) => item.demandClass === "act-now");
  const candidates = sortByCriticality(items.filter(isCandidate));
  const threshold = criticalityRank(options.minCriticality);
  const selected = candidates.filter((item) => criticalityRank(item.criticality) >= threshold);
  const deferred = [];
  for (const item of candidates) {
    if (criticalityRank(item.criticality) >= threshold) continue;
    if (selected.length < options.targetCount && item.criticality === "recommended") selected.push(item);
    else deferred.push(item);
  }
  const shortfall = options.minCount - selected.length;
  if (shortfall > 0) selected.push(...deferred.splice(0, shortfall));
  return { actNow, selected, deferred };
}
function criticalityRank(criticality) {
  return criticality ? ConsiderationCriticalityOptions.indexOf(criticality) : -1;
}
function isCandidate(item) {
  return item.demandClass === "handled" || item.demandClass === "check";
}
function sortByCriticality(items) {
  return items.map((item, index) => ({ item, index })).sort((a, b) => criticalityRank(b.item.criticality) - criticalityRank(a.item.criticality) || a.index - b.index).map(({ item }) => item);
}

// src/weekendChecklist.ts
import { CONSIDERATIONS_TITLE, LESSONS_TITLE } from "@rock/types";
import { addDaysToLocalDate as addDaysToLocalDate6 } from "@rock/util";

// src/planFirstSchedules.ts
import {
  addDaysToLocalDate as addDaysToLocalDate5,
  daysBetweenLocalDates as daysBetweenLocalDates3,
  DEFAULT_WEEKEND_START,
  isActNowCriticality,
  nextWeekendStartLocalDate,
  toLocalDate as toLocalDate3
} from "@rock/util";
var DEFAULT_EFFORT = 1;
var EFFORT_BUDGET_PER_WEEKEND = 4;
function planFirstSchedules(items, options) {
  const budget = options.effortBudgetPerWeekend ?? EFFORT_BUDGET_PER_WEEKEND;
  const firstWeekend = nextWeekendStartLocalDate(
    options.today ?? toLocalDate3(options.now),
    options.weekendStartDay ?? DEFAULT_WEEKEND_START
  );
  const sorted = items.map((item, index) => ({ item, index })).sort((a, b) => criticalityRank(b.item.criticality) - criticalityRank(a.item.criticality) || a.index - b.index);
  const plan = {};
  const spentByWeekend = preSpend(options.spent ?? [], firstWeekend);
  for (const { item } of sorted) {
    const effort = clampEffort(item.effort);
    if (isActNowCriticality(item.criticality)) {
      spentByWeekend[0] = (spentByWeekend[0] ?? 0) + effort;
      plan[item.key] = firstWeekend;
      continue;
    }
    const weekend = pickWeekend(spentByWeekend, weekendRange(item, firstWeekend), effort, budget);
    spentByWeekend[weekend] = (spentByWeekend[weekend] ?? 0) + effort;
    plan[item.key] = addDaysToLocalDate5(firstWeekend, weekend * 7);
  }
  return plan;
}
function pickWeekend(spentByWeekend, range, effort, budget) {
  const spentOn = (weekend) => spentByWeekend[weekend] ?? 0;
  for (let weekend = range.first; weekend <= range.last; weekend++) {
    if (spentOn(weekend) === 0 || spentOn(weekend) + effort <= budget) return weekend;
  }
  let lightest = range.first;
  for (let weekend = range.first + 1; weekend <= range.last; weekend++) {
    if (spentOn(weekend) < spentOn(lightest)) lightest = weekend;
  }
  return lightest;
}
function weekendRange(item, firstWeekend) {
  const first = item.notBefore ? Math.max(0, Math.ceil(daysBetweenLocalDates3(item.notBefore, firstWeekend) / 7)) : 0;
  if (!item.notAfter) return { first, last: Number.POSITIVE_INFINITY };
  const last = Math.floor(daysBetweenLocalDates3(item.notAfter, firstWeekend) / 7);
  return { first, last: Math.max(first, last) };
}
function preSpend(spent, firstWeekend) {
  const spentByWeekend = [];
  for (const entry of spent) {
    const weekend = Math.floor(daysBetweenLocalDates3(entry.date, firstWeekend) / 7);
    if (weekend < 0) continue;
    spentByWeekend[weekend] = (spentByWeekend[weekend] ?? 0) + clampEffort(entry.effort);
  }
  return spentByWeekend;
}
function clampEffort(effort) {
  if (effort === void 0 || Number.isNaN(effort)) return DEFAULT_EFFORT;
  return Math.min(EFFORT_BUDGET_PER_WEEKEND, Math.max(1, Math.round(effort)));
}

// src/taskVisibility.ts
function pausedIds(subscriptions) {
  return new Set(subscriptions.flatMap((s) => s.status === "paused" ? [s.id] : []));
}
function liveTasks(tasks, silencedReminderIds) {
  return tasks.filter(
    (task) => isTaskLive(task.status, task.subscriptionId ? !silencedReminderIds.has(task.subscriptionId) : void 0)
  );
}
function isTaskLive(taskStatus, reminderActive) {
  if (taskStatus !== "pending") {
    return true;
  }
  return reminderActive !== false;
}

// src/weekendChecklist.ts
var WEEKEND_CHECKLIST_SECTIONS = ["recalls", "actions", "recommendations", "lessons"];
var WEEKEND_CHECKLIST_SECTION_TITLES = {
  recalls: "Safety Recalls",
  actions: "Actions",
  recommendations: CONSIDERATIONS_TITLE,
  lessons: LESSONS_TITLE
};
function actionEntry(item, fields) {
  const { key, title, status, overdueLabel } = fields;
  return { section: "actions", item, key, title, done: isTaskDone(status), declined: false, overdueLabel };
}
function recommendationEntry(consideration) {
  return {
    section: "recommendations",
    item: consideration,
    key: consideration.id,
    title: consideration.title,
    done: isConsiderationDone(consideration.status),
    declined: isConsiderationDeclined(consideration.status)
  };
}
function lessonEntry(lesson) {
  return {
    section: "lessons",
    item: lesson,
    key: lesson.id,
    title: lesson.title,
    done: isLessonDone(lesson.status),
    declined: isLessonDeclined(lesson.status)
  };
}
function recallEntry(verdict) {
  return {
    section: "recalls",
    item: verdict,
    key: verdict.id,
    title: verdict.display.headline ?? verdict.display.title,
    done: isRecallVerdictDone(verdict),
    declined: isRecallVerdictDeclined(verdict)
  };
}
function sectionWeekendChecklist(entries) {
  return WEEKEND_CHECKLIST_SECTIONS.flatMap((section) => {
    const rows = entries.filter((entry) => entry.section === section);
    if (rows.length === 0) return [];
    return [{ section, title: WEEKEND_CHECKLIST_SECTION_TITLES[section], entries: rows }];
  });
}
function weekendChecklistProgress(entries) {
  const done = entries.filter((entry) => entry.done).length;
  return { total: entries.length, done, complete: entries.length > 0 && done === entries.length };
}
function isTaskDone(status) {
  return status === "completed";
}
function isConsiderationDone(status) {
  return status !== "pending";
}
function isConsiderationDeclined(status) {
  return status === "ignored";
}
function isLessonDone(status) {
  return status === "completed" || status === "dismissed";
}
function isLessonDeclined(status) {
  return status === "dismissed";
}
function isRecallVerdictDone(verdict) {
  return !!verdict.resolution;
}
function isRecallVerdictDeclined(verdict) {
  return !!verdict.resolution && verdict.resolution.outcome !== "handled";
}
function isLessonScheduledForLater(lesson, activeThrough) {
  return !isLessonDone(lesson.status) && !!lesson.reviewAt && lesson.reviewAt > activeThrough;
}
function isLessonQueued(lesson) {
  return !isLessonDone(lesson.status) && !lesson.reviewAt;
}
function isLessonUnreadThisWeek(lesson, activeThrough) {
  return lesson.status === "unread" && !!lesson.reviewAt && lesson.reviewAt <= activeThrough;
}
var DEFAULT_WEEKEND_BUDGET = {
  effortPerWeekend: EFFORT_BUDGET_PER_WEEKEND,
  firstTaskEffortPerWeekend: 2,
  effort: { actions: DEFAULT_EFFORT, recommendations: 1, lessons: 1 }
};
var WEEKEND_LOAD_HORIZON_WEEKS = 26;
function weekendLoadHorizon(today) {
  return addDaysToLocalDate6(today, WEEKEND_LOAD_HORIZON_WEEKS * 7);
}
function homeChores({ tasks, reminders }) {
  const pending = liveTasks(tasks, pausedIds(reminders)).filter((task) => task.status === "pending");
  return [...taskChores(pending, reminders), ...nextChores(reminders, pending)];
}
function taskChores(tasks, reminders) {
  const effortOf = new Map(reminders.map((reminder) => [reminder.id, reminder.effort]));
  return tasks.map((task) => ({
    date: task.taskDate,
    effort: task.subscriptionId ? effortOf.get(task.subscriptionId) : void 0
  }));
}
function nextChores(reminders, pending) {
  const carrying = new Set(pending.map((task) => task.subscriptionId));
  return reminders.flatMap((reminder) => {
    const date = reminder.nextTaskDate;
    return reminder.status === "active" && date && !carrying.has(reminder.id) ? [{ date, effort: reminder.effort }] : [];
  });
}
function choreEffort(chore, budget) {
  return chore.effort ?? budget.effort.actions;
}
function homeWeekendLoad({ today, budget, ...chores }) {
  return homeChores(chores).filter((chore) => chore.date >= today).map((chore) => ({ date: chore.date, effort: choreEffort(chore, budget) }));
}

// src/pickWeekendRows.ts
var DEFAULT_FLOOR = 1;
function pickWeekendRows(queue, options) {
  const floor = options.floor ?? DEFAULT_FLOOR;
  const picks = queue.filter(isActNowRow).map((row) => ({ row, actNow: true }));
  const waiting = sortWeekendQueue(queue.filter((row) => !isActNowRow(row)));
  const byKind = {
    recommendations: waiting.filter((row) => row.kind === "recommendations"),
    lessons: waiting.filter((row) => row.kind === "lessons")
  };
  let remaining = options.capacity;
  let picked = options.onWeekend ?? 0;
  let preferred = waiting[0]?.kind;
  while (preferred) {
    const row = byKind[preferred].shift() ?? byKind[otherKind(preferred)].shift();
    if (!row) break;
    const cost = options.effort[row.kind];
    if (picked >= floor && cost > remaining) break;
    remaining -= cost;
    picked++;
    picks.push({ row, actNow: false });
    preferred = otherKind(row.kind);
  }
  return picks;
}
function sortWeekendQueue(rows) {
  return rows.map((row, index) => ({ row, index })).sort(
    (a, b) => criticalityRank(weekendPriority(b.row)) - criticalityRank(weekendPriority(a.row)) || compareTimestamps(a.row.createdAt, b.row.createdAt) || a.index - b.index
  ).map(({ row }) => row);
}
function buildWeekendQueue(considerations, lessons) {
  return [
    ...considerations.filter((consideration) => consideration.status === "pending" && !consideration.reviewAt).map((consideration) => queueRow("recommendations", consideration)),
    ...lessons.filter(isLessonQueued).map((lesson) => queueRow("lessons", lesson))
  ];
}
function queueRow(kind, { id, criticality, createdAt }) {
  return { kind, id, criticality, createdAt };
}
function isActNowRow(row) {
  return row.kind === "recommendations" && isActNowCriticality2(row.criticality);
}
function weekendPriority(row) {
  return row.kind === "lessons" && isActNowCriticality2(row.criticality) ? "necessary" : row.criticality;
}
function otherKind(kind) {
  return kind === "recommendations" ? "lessons" : "recommendations";
}
function compareTimestamps(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

// src/planAddToList.ts
import { addDaysToLocalDate as addDaysToLocalDate8, weekendOnOffer, weekRangeContaining } from "@rock/util";

// src/taskGeneration.ts
import { TaskIdSchema, TaskSchema } from "@rock/types";
import { addDaysToLocalDate as addDaysToLocalDate7, knownTimeZone, todayInTimeZone } from "@rock/util";
var TASK_GENERATION = {
  /** An occurrence dated within this many days of the home's today gets its task now; later ones wait. */
  lookaheadDays: 14,
  /**
   * A generated task is dated at least this many days out, so it is never born overdue (e.g. when
   * the user says they last did the chore many months ago).
   */
  minDaysInFuture: 1
};
function taskGenerationDay(timeZone, now) {
  return todayInTimeZone(now, knownTimeZone(timeZone));
}
function firstTaskId(subscriptionId) {
  return TaskIdSchema.parse(`${subscriptionId}_first`);
}
function planTaskGeneration(subscription, today) {
  if (subscription.status !== "active") {
    return { kind: "idle" };
  }
  const rawTaskDate = resolveNextTaskDate(subscription);
  if (!rawTaskDate) {
    return { kind: "idle" };
  }
  const minTaskDate = addDaysToLocalDate7(today, TASK_GENERATION.minDaysInFuture);
  const taskDate = clampToMin(rawTaskDate, minTaskDate);
  if (taskDate > addDaysToLocalDate7(today, TASK_GENERATION.lookaheadDays)) {
    return { kind: "defer", nextTaskDate: taskDate };
  }
  const nextTaskDate = calculateNextTaskDate(subscription.subscription, subscription.createdAt, taskDate);
  return { kind: "due", rawTaskDate, taskDate, nextTaskDate: nextTaskDate && clampToMin(nextTaskDate, minTaskDate) };
}
function buildGeneratedTask(subscription, due, args) {
  const { taskId, nowIso, previousCompletedTaskId, taskGenerationJobRunId } = args;
  const task = TaskSchema.decode({
    object: "task",
    schema: 2,
    id: taskId,
    taskTypeId: subscription.taskTypeId,
    considerationTypeId: subscription.considerationTypeId,
    homeId: subscription.homeId,
    subscriptionId: subscription.id,
    taskGenerationJobRunId,
    spaceId: subscription.spaceId,
    previousCompletedTaskId,
    contactId: subscription.contactId,
    taskDate: due.taskDate,
    notes: subscription.taskNotes,
    createdAt: nowIso,
    updatedAt: nowIso
  });
  return {
    task,
    subscriptionUpdates: {
      lastGeneratedAt: task.createdAt,
      nextTaskDate: due.nextTaskDate,
      updatedAt: task.updatedAt
    }
  };
}
function clampToMin(date, minDate) {
  return date < minDate ? minDate : date;
}

// src/planAddToList.ts
function planAddToList(input) {
  const { cadences, lastPerformedEstimate, now, today, weekendStartDay } = input;
  const tomorrow = addDaysToLocalDate8(today, TASK_GENERATION.minDaysInFuture);
  const offer = weekendOnOffer(today, weekendStartDay);
  const { end: dueBy } = weekRangeContaining(offer, weekendStartDay);
  const own = new Map(
    cadences.map((c) => [c.taskTypeId, firstOccurrenceDate(c.subscription, now, lastPerformedEstimate)])
  );
  const planned = planWeekends({
    ...input,
    cadences: cadences.filter((c) => !laterThan(own.get(c.taskTypeId), dueBy) || !!c.subscription.window?.length)
  });
  const placements = {};
  const dates = [];
  for (const { taskTypeId } of cadences) {
    const weekend = planned[taskTypeId];
    if (weekend) placements[taskTypeId] = laterOf(weekend, tomorrow);
    dates.push(laterOf(own.get(taskTypeId) ?? null, placements[taskTypeId] ?? tomorrow));
  }
  return { placements, firstTaskDate: dates.sort()[0] ?? laterOf(offer, tomorrow) };
}
function planWeekends(input) {
  const { cadences, lastPerformedEstimate, criticality, spent, now, today, weekendStartDay, budget } = input;
  return planFirstSchedules(
    cadences.map(({ taskTypeId, subscription, effort }) => ({
      key: taskTypeId,
      criticality,
      effort: choreEffort({ effort }, budget),
      ...firstOccurrenceRange(subscription, now, lastPerformedEstimate)
    })),
    {
      now,
      // The planner offers weekends strictly after the day it is given.
      today: addDaysToLocalDate8(weekendOnOffer(today, weekendStartDay), -1),
      weekendStartDay,
      effortBudgetPerWeekend: budget.firstTaskEffortPerWeekend,
      spent
    }
  );
}
function laterOf(a, b) {
  return a && a > b ? a : b;
}
function laterThan(date, than) {
  return !!date && date > than;
}

// src/formatInterval.ts
function formatInterval(interval) {
  if (typeof interval === "number") {
    return `${interval} days`;
  }
  const { value, unit } = interval;
  if (value === 1) {
    return unit === "days" ? "1 day" : `1 ${unit.slice(0, -1)}`;
  }
  return `${value} ${unit}`;
}

// src/formatLastPerformed.ts
function formatLastPerformed(estimate) {
  switch (estimate) {
    case "last-few-weeks":
      return "A few weeks ago";
    case "last-few-months":
      return "A few months ago";
    case "within-last-year":
      return "Within the last year";
    case "more-than-a-year":
      return "More than a year ago";
    case "never":
      return "Never been done";
    case "not-sure":
      return "Unsure when";
    // Callers that have the paired `lastPerformedAt` should render the actual
    // date instead; this is only a safe fallback when the date is unavailable.
    case "specific-date":
      return "On a specific date";
  }
}

// src/taskScreenConfig.ts
var TASK_SECTION_LABELS = {
  instructions: "Instructions",
  history: "History",
  pictures: "Pictures",
  notes: "Notes",
  supplies: "Supplies",
  contact: "Contact"
};
function applyUserTaskScreenConfig(resolved, userSections) {
  if (!userSections || userSections.length === 0) {
    return resolved;
  }
  const byType = new Map(
    userSections.map((userSection, index) => [userSection.type, { enabled: userSection.enabled, index }])
  );
  return {
    sections: resolved.sections.map((section, originalIndex) => {
      const user = byType.get(section.type);
      const enabled = user?.enabled ?? section.enabled;
      const rank = user ? user.index : userSections.length + originalIndex;
      return { section: enabled === section.enabled ? section : { ...section, enabled }, rank };
    }).sort((a, b) => a.rank - b.rank).map((entry) => entry.section)
  };
}
var INITIAL_TASK_SCREEN_CONFIG = {
  sections: [
    { type: "instructions", enabled: true },
    { type: "contact", enabled: false },
    { type: "history", enabled: true },
    {
      type: "pictures",
      enabled: true,
      title: "Pictures",
      description: "Take before and after pictures so that you can compare results over time.",
      slots: [
        { category: "before", title: "Before" },
        { category: "after", title: "After" }
      ]
    },
    { type: "notes", enabled: true },
    { type: "supplies", enabled: false, title: "Supplies", suppliesConfig: [] }
  ]
};
function resolveTaskScreenConfig(taskTypeConfig, defaultConfig) {
  const defaultSections = defaultConfig;
  if (!taskTypeConfig) {
    return {
      sections: defaultSections.map((s) => stripToResolved(s))
    };
  }
  const defaultsByType = /* @__PURE__ */ new Map();
  for (const section of defaultSections) {
    defaultsByType.set(section.type, section);
  }
  const coveredTypes = /* @__PURE__ */ new Set();
  const sections = taskTypeConfig.sections.map((ttSection) => {
    coveredTypes.add(ttSection.type);
    const defaultSection = defaultsByType.get(ttSection.type);
    if (ttSection.useDefault && defaultSection) {
      return inheritFromDefault(ttSection, defaultSection);
    }
    if (ttSection.type === "pictures") {
      const fallbackSlots = defaultSection?.type === "pictures" ? defaultSection.slots : [];
      return {
        type: "pictures",
        enabled: ttSection.enabled,
        title: ttSection.title,
        description: ttSection.description,
        slots: ttSection.slots ?? fallbackSlots
      };
    }
    if (ttSection.type === "supplies") {
      const fallbackConfig = defaultSection?.type === "supplies" ? defaultSection.suppliesConfig : [];
      return {
        type: "supplies",
        enabled: ttSection.enabled,
        title: ttSection.title,
        description: ttSection.description,
        suppliesConfig: ttSection.suppliesConfig ?? fallbackConfig
      };
    }
    return {
      type: ttSection.type,
      enabled: ttSection.enabled,
      title: ttSection.title,
      description: ttSection.description
    };
  });
  for (const defaultSection of defaultSections) {
    if (!coveredTypes.has(defaultSection.type)) {
      sections.push(stripToResolved(defaultSection));
    }
  }
  return { sections };
}
function omitUnavailableSections(config, taskType) {
  if (hasInstructions(taskType)) {
    return config;
  }
  return { sections: config.sections.filter((section) => section.type !== "instructions") };
}
function hasInstructions(taskType) {
  return !!taskType.taskDocId;
}
function inheritFromDefault(ttSection, defaultSection) {
  if (ttSection.type === "pictures" && defaultSection.type === "pictures") {
    return {
      type: "pictures",
      enabled: ttSection.enabled,
      title: defaultSection.title,
      description: defaultSection.description,
      slots: defaultSection.slots
    };
  }
  if (ttSection.type === "supplies" && defaultSection.type === "supplies") {
    return {
      type: "supplies",
      enabled: ttSection.enabled,
      title: defaultSection.title,
      description: defaultSection.description,
      suppliesConfig: defaultSection.suppliesConfig
    };
  }
  return {
    type: ttSection.type,
    enabled: ttSection.enabled,
    title: defaultSection.title,
    description: defaultSection.description
  };
}
function stripToResolved(section) {
  const { defaultEnabled, ...rest } = section;
  return { ...rest, enabled: defaultEnabled ?? rest.enabled };
}

// src/taskSubscriptionsEqual.ts
function taskSubscriptionsEqual(a, b) {
  if (!a || !b) return !a && !b;
  return a.type === b.type && intervalsEqual(a.interval, b.interval) && intervalsEqual(a.offset, b.offset) && windowsEqual(a.window, b.window);
}
function intervalsEqual(a, b) {
  if (!a || !b) return !a && !b;
  return a.value === b.value && a.unit === b.unit;
}

// src/weekendListing.ts
import { isQuietFyi } from "@rock/types";
import { hasReviewAt, isAnsweredSince, selectWeekendReviews } from "@rock/util";
function isLessonResolvedSince(lesson, since) {
  return [lesson.completedAt, lesson.dismissedAt].some((stamp) => !!stamp && stamp >= since);
}
function isTaskResolvedSince(task, since) {
  return task.status === "completed" && !!task.completedAt && task.completedAt >= since;
}
function listedConsiderations(considerations, { range, since }) {
  const dated = considerations.filter(hasReviewAt).filter((c) => c.status !== "withdrawn");
  return selectWeekendReviews(dated, range, {
    isOpen: (c) => !isConsiderationDone(c.status),
    isResolvedInRange: (c) => isAnsweredSince(c, since)
  });
}
function listedLessons(lessons, { range, since }) {
  return selectWeekendReviews(lessons.filter(hasReviewAt), range, {
    isOpen: (lesson) => !isLessonDone(lesson.status),
    isResolvedInRange: (lesson) => isLessonResolvedSince(lesson, since)
  });
}
function listedTasks(tasks, { range, since }) {
  const rows = tasks.filter((task) => task.status === "pending" || task.status === "completed").map((task) => ({ task, reviewAt: task.taskDate }));
  return selectWeekendReviews(rows, range, {
    isOpen: ({ task }) => task.status === "pending",
    isResolvedInRange: ({ task }) => isTaskResolvedSince(task, since)
  }).map(({ task }) => task);
}
function listedRecallVerdicts(verdicts, { since }) {
  return verdicts.filter(
    (verdict) => !verdict.withdrawn && !isQuietFyi(verdict) && (!verdict.resolution || verdict.resolution.at >= since)
  );
}

// src/weekendCarry.ts
function weekendCarry(input) {
  const { window, budget, placedChores = [] } = input;
  const chores = [...listedChores(input, window), ...placedChores];
  const recommendations = listedConsiderations(input.considerations, window).length;
  const lessons = listedLessons(input.lessons, window).length;
  const { effort } = budget;
  const choreSlots = chores.reduce((sum, chore) => sum + choreEffort(chore, budget), 0);
  const taken = choreSlots + recommendations * effort.recommendations + lessons * effort.lessons;
  return { chores: chores.length, extras: recommendations + lessons, capacity: budget.effortPerWeekend - taken };
}
function listedChores({ tasks, reminders }, window) {
  const live = liveTasks(tasks, pausedIds(reminders));
  const pending = live.filter((task) => task.status === "pending");
  return [
    ...taskChores(listedTasks(live, window), reminders),
    ...nextChores(reminders, pending).filter((chore) => chore.date <= window.range.end)
  ];
}
export {
  COMING_UP_WINDOW_DAYS,
  DEFAULT_EFFORT,
  DEFAULT_WEEKEND_BUDGET,
  EFFORT_BUDGET_PER_WEEKEND,
  INITIAL_TASK_SCREEN_CONFIG,
  SEASON_PART_MONTHS,
  TASK_GENERATION,
  TASK_SECTION_LABELS,
  WEEKEND_CHECKLIST_SECTIONS,
  WEEKEND_CHECKLIST_SECTION_TITLES,
  actionEntry,
  addInterval,
  applyUserTaskScreenConfig,
  buildGeneratedTask,
  buildWeekendQueue,
  calculateNextTaskDate,
  cascadeReschedule,
  choreEffort,
  clampTaskDateForward,
  criticalityRank,
  estimatedLastPerformedAt,
  firstOccurrenceDate,
  firstOccurrenceRange,
  firstTaskId,
  formatCadenceWindow,
  formatInterval,
  formatLastPerformed,
  hasInstructions,
  homeChores,
  homeWeekendLoad,
  isComingUpChore,
  isConsiderationDeclined,
  isConsiderationDone,
  isInWindow,
  isLessonDeclined,
  isLessonDone,
  isLessonQueued,
  isLessonResolvedSince,
  isLessonScheduledForLater,
  isLessonUnreadThisWeek,
  isRecallVerdictDeclined,
  isRecallVerdictDone,
  isTaskDone,
  isTaskLive,
  isTaskResolvedSince,
  lastPerformedEstimateToInterval,
  lessonEntry,
  listedConsiderations,
  listedLessons,
  listedRecallVerdicts,
  listedTasks,
  liveTasks,
  nextChores,
  nextOccurrenceAfter,
  omitUnavailableSections,
  pausedIds,
  pickWeekendRows,
  planAddToList,
  planFirstSchedules,
  planReviewSelection,
  planTaskGeneration,
  recallEntry,
  recommendationEntry,
  resolveLastPerformedAt,
  resolveNextTaskDate,
  resolveTaskScreenConfig,
  seasonLabel,
  sectionWeekendChecklist,
  snapToWindow,
  sortWeekendQueue,
  taskChores,
  taskGenerationDay,
  taskSubscriptionsEqual,
  upcomingTaskDate,
  weekendCarry,
  weekendChecklistProgress,
  weekendLoadHorizon,
  windowFitsCadence,
  windowRunEnd,
  windowsEqual,
  withFittingWindow
};
//# sourceMappingURL=index.js.map